/**
 * SDA-120: variable allowlist per campaign template name.
 *
 * The TemplatesTab editor uses this to filter the variable picker dropdown so
 * admins can only insert {{vars}} that are valid for the template type they're
 * editing. Send-time defensive belt (SDA-131) strips any unresolved {{tag}} as
 * a safety net — but UX-wise we want the picker to never offer an invalid var.
 *
 * Names match `email_templates.name` for rows where `template_kind='campaign'`.
 */
export const VARS_BY_TEMPLATE_NAME: Record<string, string[]> = {
  tour_announcement: [
    'show_name',
    'city',
    'state',
    'theater_name',
    'available_dates',
    'available_dates_short',
    'time_slots',
    'pricing',
    'deposit_amount',
    'booking_url',
    'upcoming_shows',
    'upcoming_shows_block',
    'contact_email',
    'contact_phone',
    'contact_name',
    'studio_name',
  ],
  reminder: [
    'show_name',
    'city',
    'available_dates',
    'pricing',
    'booking_url',
    'contact_name',
    'studio_name',
  ],
  final_reminder: [
    'show_name',
    'city',
    'available_dates',
    'pricing',
    'booking_url',
    'contact_name',
    'studio_name',
  ],
  activation: [
    'contact_name',
    'studio_name',
    'activation_url',
    'contact_email',
    'contact_phone',
    'city',
    'state',
  ],
  faculty_invitation_campaign: [
    'faculty_name',
    'show_name',
    'invitation_url',
    'contact_email',
    'contact_phone',
  ],
};

/**
 * SDA-137: variable allowlist for user-created custom campaign templates.
 * Custom templates have arbitrary names that won't appear in
 * VARS_BY_TEMPLATE_NAME, so the picker falls back to this default set.
 * Keep these to the common, broadly-available vars that resolve for any
 * (contact, studio, campaign) tuple — no show/tour/faculty-specific tokens.
 */
export const DEFAULT_CUSTOM_VARS = [
  'first_name',
  'contact_name',
  'studio_name',
  'show_name',
  'city',
  'state',
  'theater_name',
  'available_dates',
  'available_dates_short',
  'time_slots',
  'pricing',
  'deposit_amount',
  'questionnaire_url',
  'contact_email',
  'contact_phone',
  'upcoming_shows',
  'upcoming_shows_block',
  'booking_url',
  'unsubscribe_url',
] as const;

/** Variables resolved at send-time per recipient (not from campaign config).
 *  Appearing as {{name}} in the preview is EXPECTED — they fill in at send.
 *  Source: packages/workers/src/cron/index.ts:1800-1815 (substitution loop). */
export const PER_RECIPIENT_VARS = [
  'first_name',
  'contact_name',
  'studio_name',
  'studio_zip',
  'booking_url',
  'unsubscribe_url',
  'activation_url',
  'invitation_url',
  'faculty_name',
] as const;

export type PerRecipientVar = (typeof PER_RECIPIENT_VARS)[number];

/** Variables derived from the target SHOW (+ its tour stop). Empty without a target show.
 *  Source: resolveCampaignTemplateVariables (packages/workers/src/services/campaign-templates.ts). */
export const SHOW_DEPENDENT_VARS = [
  'show_name',
  'theater_name',
  'available_dates',
  'available_dates_short',
  'pricing',
  'upcoming_shows',
  'upcoming_shows_block',
] as const;

/** Fill from a show's tour-stop city OR (when there's no show) a SINGLE targeted city. */
export const CITY_DEPENDENT_VARS = ['city', 'state'] as const;

/** Always resolve from global settings / hard-coded values — fill regardless of targeting. */
export const ALWAYS_AVAILABLE_VARS = [
  'time_slots',
  'deposit_amount',
  'contact_email',
  'contact_phone',
  'questionnaire_url',
] as const;

/**
 * Given the {{tokens}} used in a campaign and the current targeting, returns the
 * subset that will render EMPTY in the sent email — so the composer can warn the
 * admin specifically. Per-recipient and always-available vars never appear here.
 */
export function emptyVariablesForTargeting(
  tokensUsed: string[],
  targeting: { hasShow: boolean; singleCity: boolean },
): string[] {
  const showSet = new Set<string>(SHOW_DEPENDENT_VARS);
  const citySet = new Set<string>(CITY_DEPENDENT_VARS);
  const empty: string[] = [];
  for (const t of tokensUsed) {
    if (showSet.has(t) && !targeting.hasShow) empty.push(t);
    // city/state fill from a show's tour stop OR exactly one targeted city.
    else if (citySet.has(t) && !targeting.hasShow && !targeting.singleCity) empty.push(t);
  }
  return empty;
}

/**
 * SDA-192: mirror of the backend TOKEN_FALLBACKS (campaign-templates.ts). When a
 * KNOWN token can't be resolved with the current targeting it renders this
 * friendly value instead of a blank gap. Tokens not listed here render BLANK.
 * Keep in sync with the worker registry.
 */
export const TOKEN_FALLBACK_LABELS: Record<string, string> = {
  city: 'your area',
  show_name: 'our upcoming Broadway Master Class',
  theater_name: 'a nearby venue',
  state: '(blank)',
  available_dates_short: '(blank)',
};

/** What a {{token}} that won't resolve under the current targeting will render
 *  as when sent: its friendly fallback, or "(blank)" for everything else. */
export function fallbackRenderLabel(token: string): string {
  return TOKEN_FALLBACK_LABELS[token] ?? '(blank)';
}

/** Human-readable description per variable name for the picker UI. */
export const VARIABLE_DESCRIPTIONS: Record<string, string> = {
  show_name: 'Name of the target show',
  city: 'City name',
  state: 'State abbreviation',
  theater_name: 'Venue/theater name',
  available_dates: 'Saturdays in the tour stop date range',
  available_dates_short: 'Compact date range, e.g. "June/July, 2025"',
  time_slots: 'Session time range, e.g. "9:30 AM – 11:00 AM"',
  pricing: 'Starting price for classes',
  deposit_amount: 'Deposit required per class ($100)',
  booking_url: "Link to this campaign's event page (uses studio zip code)",
  upcoming_shows: "Comma-separated list of other upcoming shows in this campaign's city",
  upcoming_shows_block: "Heading + list of other upcoming shows in this campaign's city",
  contact_email: 'Contact email address',
  contact_phone: 'Contact phone number',
  contact_name: "Recipient contact's first name",
  studio_name: "Recipient studio's name",
  activation_url: 'Personalized account activation link',
  faculty_name: 'Faculty member name',
  invitation_url: 'Personalized faculty invitation link',
  first_name: "Recipient's first name",
  unsubscribe_url: 'Personalized unsubscribe link',
  questionnaire_url: 'Pre-class questionnaire link (events page)',
};

/** Sample values for in-editor preview rendering. Mirrors PREVIEW_SAMPLE_VALUES from CampaignFormPage. */
export const PREVIEW_SAMPLE_VALUES: Record<string, string> = {
  show_name: '[Show Name]',
  city: '[City]',
  state: '[ST]',
  theater_name: '[Theater]',
  available_dates: 'Saturdays: June 21, June 28, July 5',
  available_dates_short: 'June/July, 2025',
  time_slots: '9:30 AM – 11:00 AM',
  pricing: '$650',
  deposit_amount: '$100',
  booking_url: 'https://stagedooraccess.com/events/8f14e45f-ceea-467f-9a2d-3b1c5d8e6a01?zip=10036',
  upcoming_shows: 'Hairspray – June 14, [Same City]<br>Wicked – July 5, [Same City]',
  upcoming_shows_block:
    '<p style="font-size:15px;font-weight:700;margin:20px 0 4px 0;color:#222;"><strong>Upcoming Broadway Master Classes:</strong></p><p style="margin:0 0 20px 0;">Hairspray – June 14, [Same City]<br>Wicked – July 5, [Same City]</p>',
  contact_email: 'michele@stagedooraccess.com',
  contact_phone: '(415) 846-9450',
  contact_name: 'Jane',
  studio_name: 'Your Dance Studio',
  activation_url: 'https://stagedooraccess.com/activate?token=sample-token',
  faculty_name: 'Jane Doe',
  invitation_url: 'https://stagedooraccess.com/faculty/invite?token=sample-token',
  first_name: 'Jane',
  unsubscribe_url: 'https://stagedooraccess.com/unsubscribe?token=sample-token',
  questionnaire_url: 'https://stagedooraccess.com/events',
};
